import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3887c62b-7cac-4b2d-b17d-6e46f80903f9/artifacts/pcgjlywb_Untitled%20design%20%281%29.png";

export const Layout = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll to top on page change (except when returning to a game via hash)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const navLinks = [
    { path: "/", label: "Games" },
    { path: "/crowdfunding", label: "Crowdfunding" },
    { path: "/about", label: "About IBG" },
    { path: "/submit-game", label: "Get Your Game Listed" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              data-testid="logo-link"
            >
              <img 
                src={LOGO_URL} 
                alt="Independent Boardgames Logo" 
                className="w-14 h-14 rounded-xl group-hover:scale-105 transition-transform"
              />
              <div>
                <h1 className="font-serif font-bold text-xl text-slate-900 tracking-tight">
                  Independent Board Games
                </h1>
                <p className="text-xs text-slate-500 font-sans">Discover Self-Published Tabletop</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link font-sans font-medium text-sm ${
                    isActive(link.path)
                      ? "text-[#7C3AED]"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav 
            className="md:hidden bg-white border-t border-stone-200 py-4 px-6 animate-slide-in"
            data-testid="mobile-nav"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-3 font-sans font-medium ${
                  isActive(link.path)
                    ? "text-[#7C3AED]"
                    : "text-slate-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#7C3AED] text-white py-16 mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={LOGO_URL} 
                  alt="Independent Boardgames Logo" 
                  className="w-12 h-12 rounded-xl"
                />
                <span className="font-serif font-bold text-lg">Independent Boardgames</span>
              </div>
              <p className="text-slate-300 font-sans text-sm leading-relaxed">
                Showcasing the best self-published board games from creators around the world.
              </p>
            </div>
            <div>
              <h3 className="font-serif font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-slate-300 font-sans text-sm">
                <li><Link to="/" className="hover:text-white transition-colors">Browse Games</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">About IBG</Link></li>
                <li><Link to="/submit-game" className="hover:text-white transition-colors">Get Your Game Listed</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-serif font-bold mb-4">Contact</h3>
              <p className="text-slate-300 font-sans text-sm mb-4">
                <a 
                  href="mailto:garethsherwood@yahoo.com" 
                  className="hover:text-white transition-colors"
                >
                  garethsherwood@yahoo.com
                </a>
              </p>
              <a 
                href="https://youtube.com/@independentboardgames" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors font-sans text-sm"
              >
                <svg 
                  className="w-6 h-6" 
                  fill="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                @independentboardgames
              </a>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-12 pt-8 text-center text-slate-400 text-sm font-sans">
            <p>&copy; {new Date().getFullYear()} Independent Boardgames. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Hidden Admin Access - tiny logo in bottom right */}
      <Link 
        to="/admin" 
        className="fixed bottom-4 right-4 opacity-20 hover:opacity-40 transition-opacity z-50"
        aria-label="Admin"
      >
        <img 
          src={LOGO_URL} 
          alt="" 
          className="w-6 h-6 rounded"
        />
      </Link>
    </div>
  );
};

export default Layout;
